import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, AlertCircle, ChevronDown, Lock,
  Award, PieChart, Loader2, BookOpen, Eye, Layers, CheckCircle
} from 'lucide-react';
import api from '../../api';
import PaperTemplateModal from './PaperTemplateModal';
import QuestionEditorModal from './QuestionEditorModal';

function FeedbackModal({ open, message, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-100 p-5 max-w-sm w-full">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg shrink-0">
            <AlertCircle className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h4 className="text-xs font-bold tracking-wider text-slate-900 uppercase">System Notification</h4>
            <p className="text-slate-600 text-xs mt-1 leading-relaxed font-normal">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button
            className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
            onClick={onClose}
          >
            Acknowledge
          </button>
        </div>
      </div>
    </div>
  );
}

function SimpleMarksEntryModal({ open, onClose, assignment, onSave, isEndExam = false }) {
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStudentsAndMarks = useCallback(async () => {
    setLoading(true);
    try {
      const apiUrl = isEndExam 
        ? `/api/faculty/end-exam-marks/${assignment._id}/students`
        : `/api/faculty/marks-entry/${assignment._id}/students`;
      
      console.log(`[SimpleMarksEntryModal] Fetching students from: ${apiUrl}`);
      const response = await api.get(apiUrl);
      if (response.data.success) {
        const studentsData = response.data.data.students;
        setStudents(studentsData);
        const initialMarks = {};
        studentsData.forEach(student => {
          initialMarks[student.studentId] = {
            obtained: student.totalMarksObtained || 0,
            max: student.totalMaxMarks || assignment.totalMaxMarks || 100
          };
        });
        setMarks(initialMarks);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  }, [assignment, isEndExam]);

  useEffect(() => {
    if (open && assignment) {
      fetchStudentsAndMarks();
    }
  }, [open, assignment, fetchStudentsAndMarks]);

  const handleMarkChange = (studentId, value) => {
    const numValue = parseFloat(value) || 0;
    setMarks(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        obtained: Math.min(numValue, prev[studentId]?.max || 100)
      }
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      for (const [studentId, markData] of Object.entries(marks)) {
        const student = students.find(s => s.studentId === studentId);
        if (student && markData.obtained !== student.totalMarksObtained) {
          const apiUrl = isEndExam 
            ? `/api/faculty/end-exam-marks/${assignment._id}/students/${studentId}`
            : `/api/faculty/marks-entry/${assignment._id}/students/${studentId}`;
          
          await api.post(apiUrl, {
            marksData: {
              single_question: {
                marksObtained: markData.obtained,
                maxMarks: markData.max
              }
            }
          });
        }
      }
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving marks:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xl w-full max-w-2xl h-full max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center shrink-0">
          <div>
            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest block font-mono">Academic Ledger</span>
            <h3 className="font-bold text-sm text-slate-800 tracking-tight">{assignment?.subjectName}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 text-sm font-semibold">✕</button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-5 bg-white">
          {loading ? (
            <div className="text-center py-16 flex flex-col items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
              <p className="text-xs text-slate-400 mt-2 font-medium">Synchronizing roster database...</p>
            </div>
          ) : (
            <div className="border border-slate-100 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100 text-slate-500 font-bold text-[10px] uppercase tracking-wider">
                    <th className="px-4 py-2.5 font-semibold">USN Reference</th>
                    <th className="px-4 py-2.5 font-semibold">Student Name</th>
                    <th className="px-4 py-2.5 text-center w-28 font-semibold">Score Awarded</th>
                    <th className="px-4 py-2.5 text-center w-16 font-semibold">Max</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                  {students.map(student => (
                    <tr key={student.studentId} className="hover:bg-slate-50/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-slate-600 font-semibold tracking-wide">{student.studentUSN}</td>
                      <td className="px-4 py-2.5 font-medium text-slate-900">{student.studentName}</td>
                      <td className="px-4 py-1 text-center">
                        <input
                          type="number"
                          value={marks[student.studentId]?.obtained || 0}
                          onChange={(e) => handleMarkChange(student.studentId, e.target.value)}
                          className="w-16 h-6 px-1.5 border border-slate-200 rounded-md text-center font-mono text-xs focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
                          step="0.5"
                          min="0"
                          max={marks[student.studentId]?.max || 100}
                        />
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono text-slate-400 font-medium">
                        {marks[student.studentId]?.max || 100}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2.5 shrink-0">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="px-4 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-all min-w-[110px] shadow-sm"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : 'Commit Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// UPDATED: AssessmentStructureViewer with batchId support
// ============================================================
const AssessmentStructureViewer = ({ 
  subjectId, 
  subjectName, 
  facultyId, 
  isActive, 
  classItem, 
  subject, 
  programTypeId, 
  programId,
  batchId,        // ✅ NEW: batchId for lab filtering
  batchNumber,    // ✅ NEW: batchNumber for lab filtering
  labBatch        // ✅ NEW: full labBatch object
}) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [isPrefetching, setIsPrefetching] = useState(true);
  const [error, setError] = useState(null);
  const [structure, setStructure] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState({});
  const [expandedEndExamItems, setExpandedEndExamItems] = useState({});
  const [feedback, setFeedback] = useState({ open: false, message: '' });
  
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [questionEditorOpen, setQuestionEditorOpen] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [paperContext, setPaperContext] = useState({});
  const [checkingAssignment, setCheckingAssignment] = useState(false);
  const [assignmentsByKey, setAssignmentsByKey] = useState({});
  const [simpleMarksAssignment, setSimpleMarksAssignment] = useState(null);
  const [isEndExamAssignment] = useState(false);
  
  const [localProgramTypeId, setLocalProgramTypeId] = useState(programTypeId);
  const [localProgramId, setLocalProgramId] = useState(programId);

  // Log batchId for debugging
  console.log('[AssessmentStructureViewer] 🔍 Received props:', {
    batchId,
    batchNumber,
    labBatch: labBatch ? { batchId: labBatch.batchId, batchNumber: labBatch.batchNumber } : null,
    subjectName
  });

  const classItemId = classItem?.classId || classItem?._id;
  const itemProgramTypeId = classItem?.programTypeId;
  const itemProgramId = classItem?.programId;
  const hasSemesterId = !!(
    subject?.semesterId ||
    subject?.semId ||
    classItem?.semesterId ||
    classItem?.semId ||
    classItem?.semesterInfo?._id
  );
  const endExamTitle = hasSemesterId ? 'Semester End Examination (SEE)' : 'End Examination';
  const endExamValueLabel = hasSemesterId ? 'Subject SEE Marks' : 'Subject End Exam Marks';

  const fetchAssessmentStructure = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[AssessmentStructureViewer] Fetching assessment structure...');
      console.log('[AssessmentStructureViewer] Using classId:', classItem?.classId);
      
      const response = await api.get(`/api/faculty/assessment/subject/${subjectId}/assessment-structure`, {
        params: { 
          classId: classItem?.classId,
          batchId: batchId,        
          batchNumber: batchNumber  
        }
      });
      
      if (response.data.success) {
        console.log('[AssessmentStructureViewer] Assessment structure fetched successfully');
        
        const structureData = response.data.data;
        setStructure(structureData);
        
        const expanded = {};
        
        if (structureData.components && structureData.components.length > 0) {
          structureData.components.forEach((comp, idx) => {
            const id = comp.componentId || comp.id || idx;
            expanded[id] = true;
          });
        }
        
        expanded['end-exam'] = true;
        
        if (structureData.endExam?.hasMultipleItems && structureData.endExam.items) {
          structureData.endExam.items.forEach(item => {
            const itemId = item.id || item.itemId;
            if (itemId) {
              expanded[`end-exam-item-${itemId}`] = true;
            }
          });
        }
        
        setExpandedComponents(expanded);
        
        return structureData;
      } else {
        console.warn('[AssessmentStructureViewer] API returned success: false');
        setError(response.data.message || 'Failed to resolve syllabus matrix bounds.');
        return null;
      }
    } catch (err) {
      console.error('Error fetching assessment structure:', err);
      setError('Curriculum repository structure mapping boundary execution failure.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [subjectId, classItem?.classId, batchId, batchNumber]);

  useEffect(() => {
    const fetchProgramDetails = async () => {
      if ((!localProgramTypeId || !localProgramId) && classItemId && isActive) {
        try {
          if (itemProgramTypeId && itemProgramId) {
            setLocalProgramTypeId(itemProgramTypeId);
            setLocalProgramId(itemProgramId);
            return;
          }
          if (classItemId) {
            const response = await api.get(`/api/classes/${classItemId}`);
            if (response.data.success) {
              const classData = response.data.data;
              setLocalProgramTypeId(classData.programTypeId);
              setLocalProgramId(classData.programId);
            }
          }
        } catch (err) {
          console.error('Failed to fetch program details:', err);
        }
      }
    };
    fetchProgramDetails();
  }, [classItemId, itemProgramTypeId, itemProgramId, localProgramTypeId, localProgramId, isActive]);

const hasMarksEntered = useCallback(async (assignmentId, isEndExam = false) => {
  try {
    // ✅ Build URL with batch params
    let apiUrl = isEndExam 
      ? `/api/faculty/end-exam-marks/${assignmentId}/students`
      : `/api/faculty/marks-entry/${assignmentId}/students`;
    
    const params = new URLSearchParams();
    if (batchId) {
      params.append('batchId', batchId);
    }
    if (batchNumber) {
      params.append('batchNumber', batchNumber);
    }
    
    const queryString = params.toString();
    if (queryString) {
      apiUrl += `?${queryString}`;
    }
    
    console.log(`[hasMarksEntered] 📡 Checking marks at: ${apiUrl}`);
    
    const response = await api.get(apiUrl);
    if (response.data.success) {
      const students = response.data.data.students || [];
      return students.some(student => student.totalMarksObtained > 0);
    }
    return false;
  } catch (error) {
    console.error('Error checking marks:', error);
    return false;
  }
}, [batchId, batchNumber]); // ✅ Added batchId and batchNumber to dependencies

  useEffect(() => {
    let cancelled = false;

    const fetchAllAssignments = async (structureData) => {
      console.log('[PREFETCH] 🚀 fetchAllAssignments STARTED');
      
      if (!structureData || !classItem?.classId || !subjectId) {
        console.log('[PREFETCH] ❌ Early exit - missing required data');
        setIsPrefetching(false);
        return;
      }
      
      setIsPrefetching(true);
      const assignments = {};
      const requests = [];
      
      console.log('[PREFETCH] ========== STARTING ASSIGNMENT PREFETCH ==========');
      console.log('[PREFETCH] Class ID:', classItem?.classId);
      console.log('[PREFETCH] Subject ID:', subjectId);
      
      structureData.components?.forEach(component => {
        const normalizedComponentId = String(
          component.componentId || component.id || component._id || ''
        );
        
        if (!normalizedComponentId) {
          console.warn('[PREFETCH] ⚠️ Component has no ID:', component.name);
          return;
        }
        
        if (component.items && Array.isArray(component.items) && component.items.length > 0) {
          component.items.forEach(item => {
            const normalizedItemId = String(item.itemId || item.id || item._id || '');
            
            requests.push({
              componentId: normalizedComponentId,
              itemId: normalizedItemId,
              classId: String(classItem.classId),
              subjectId: String(subjectId),
              isEndExam: false
            });
          });
        } else if (component.type === 'single') {
          requests.push({
            componentId: normalizedComponentId,
            itemId: '',
            classId: String(classItem.classId),
            subjectId: String(subjectId),
            isEndExam: false
          });
        }
      });

      if (structureData.endExam) {
        const endExamComponentId = String(
          structureData.endExam.componentId || 
          structureData.endExam.id || 
          structureData.endExam._id || 
          ''
        );
        
        console.log('[PREFETCH] End Exam structure detected:', {
          id: structureData.endExam.id,
          _id: structureData.endExam._id,
          componentId: structureData.endExam.componentId,
          endExamComponentId: endExamComponentId,
          hasMultipleItems: structureData.endExam.hasMultipleItems,
          itemsCount: structureData.endExam.items?.length
        });
        
        if (!endExamComponentId) {
          console.warn('[PREFETCH] ⚠️ End Exam has no component ID');
        } else if (structureData.endExam.hasMultipleItems && structureData.endExam.items && structureData.endExam.items.length > 0) {
          structureData.endExam.items.forEach(item => {
            const normalizedItemId = String(item.itemId || item.id || item._id || '');
            
            console.log('[PREFETCH] Adding End Exam item request:', {
              componentId: endExamComponentId,
              itemId: normalizedItemId,
              itemName: item.name
            });
            
            requests.push({
              componentId: endExamComponentId,
              itemId: normalizedItemId,
              classId: String(classItem.classId),
              subjectId: String(subjectId),
              isEndExam: true
            });
          });
        } else if (endExamComponentId) {
          console.log('[PREFETCH] Adding End Exam single item request:', {
            componentId: endExamComponentId,
            itemId: '',
            isEndExam: true
          });
          requests.push({
            componentId: endExamComponentId,
            itemId: '',
            classId: String(classItem.classId),
            subjectId: String(subjectId),
            isEndExam: true
          });
        }
      }
      
      console.log('[PREFETCH] Total requests to process:', requests.length);
      console.log('[PREFETCH] CIE requests:', requests.filter(r => !r.isEndExam).length);
      console.log('[PREFETCH] End Exam requests:', requests.filter(r => r.isEndExam).length);
      
      if (requests.length === 0) {
        console.log('[PREFETCH] ⚠️ No requests to process, completing immediately');
        setAssignmentsByKey({});
        setIsPrefetching(false);
        console.log('[PREFETCH] ========== ASSIGNMENT PREFETCH COMPLETE (NO REQUESTS) ==========');
        return;
      }
      
      try {
        await Promise.all(requests.map(async (params) => {
          try {
            const assignmentEndpoint = params.isEndExam
              ? '/api/faculty/end-exam-paper-templates/assignment'
              : '/api/faculty/paper-template-assignment';
            
            console.log(`[PREFETCH] 📡 Calling ${assignmentEndpoint} with params:`, {
              componentId: params.componentId,
              itemId: params.itemId,
              classId: params.classId,
              subjectId: params.subjectId,
              isEndExam: params.isEndExam
            });
            
            const res = await api.get(assignmentEndpoint, { params });
            
            if (res.data.success && res.data.assignment) {
              const key = `${params.componentId}__${params.itemId}`;
              const hasMarks = await hasMarksEntered(res.data.assignment._id, params.isEndExam || false);
              console.log(`[PREFETCH] ✅ Assignment FOUND for key: ${key}, hasMarks: ${hasMarks}`);
              assignments[key] = {
                ...res.data.assignment,
                hasMarksEntered: hasMarks,
                isEndExam: params.isEndExam || false
              };
            } else {
              console.log(`[PREFETCH] ❌ No assignment found for component: ${params.componentId}, item: ${params.itemId}, isEndExam: ${params.isEndExam}`);
            }
          } catch (err) {
            if (err.response?.status === 404) {
              console.log(`[PREFETCH] ℹ️ 404 - No assignment for component: ${params.componentId}, item: ${params.itemId}`);
            } else if (err.response?.status === 400) {
              console.warn(`[PREFETCH] ⚠️ 400 - Bad request for component: ${params.componentId}, item: ${params.itemId}`, err.response?.data);
            } else {
              console.error('[PREFETCH] ❌ Framework item tracking failure:', err.message);
            }
          }
        }));
        
        console.log('[PREFETCH] ✅ All requests processed');
        
        if (!cancelled) {
          setAssignmentsByKey(assignments);
          console.log('[PREFETCH] Final assignmentsByKey:', Object.keys(assignments));
        }
      } catch (err) {
        console.error('[PREFETCH] ❌ Lifecycle background tracking fault:', err);
      } finally {
        console.log('[PREFETCH] 🔚 Finally block - cancelled:', cancelled);
        if (!cancelled) {
          console.log('[PREFETCH] ✅ Setting isPrefetching to false');
          setIsPrefetching(false);
          console.log('[PREFETCH] ========== ASSIGNMENT PREFETCH COMPLETE ==========');
        } else {
          console.log('[PREFETCH] ⚠️ Skipping setIsPrefetching(false) because cancelled is true');
        }
      }
    };

    if (isActive && subjectId) {
      (async () => {
        const data = await fetchAssessmentStructure();
        if (data) {
          await fetchAllAssignments(data);
        } else {
          setIsPrefetching(false);
        }
      })();
    }
    
    return () => { cancelled = true; };
  }, [isActive, subjectId, classItemId, classItem?.classId, fetchAssessmentStructure, hasMarksEntered]);

  const toggleComponent = (componentId) => {
    setExpandedComponents(prev => ({
      ...prev,
      [componentId]: !prev[componentId]
    }));
  };

  const toggleEndExamItem = (itemId) => {
    setExpandedEndExamItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // ============================================================
  // UPDATED: navigateToMarksEntry with batchId
  // ============================================================
  const navigateToMarksEntry = (assignment, componentId = null, itemId = null, readOnly = false, isEndExam = false) => {
    console.log(`[AssessmentStructureViewer] Navigating to marks entry - isEndExam: ${isEndExam}, assignmentId: ${assignment?._id}`);
    console.log(`[AssessmentStructureViewer] 🔍 Passing batchId: ${batchId}, batchNumber: ${batchNumber}`);
    
    navigate('/faculty/mark-entry', {
      state: {
        assignmentId: assignment._id,
        assignment: assignment,
        subjectId: subjectId,
        subjectName: subjectName,
        classId: classItem?.classId,
        componentId: componentId,
        itemId: itemId,
        readOnly: readOnly,
        isEndExam: isEndExam,
        // ✅ PASS BATCH INFO FOR LAB FILTERING
        batchId: batchId,
        batchNumber: batchNumber,
        labBatch: labBatch
      }
    });
  };

  // ============================================================
  // UPDATED: createAndNavigateToMarksEntry with batchId
  // ============================================================
  const createAndNavigateToMarksEntry = async (component, item, isEndExam = false) => {
    setCheckingAssignment(true);
    try {
      const classId = classItem?.classId;
      const tenantId = classItem?.academicYear?.tenantId || classItem?.tenantId || localStorage.getItem('tenantId');
      const finalProgramTypeId = localProgramTypeId || programTypeId;
      const finalProgramId = localProgramId || programId;
      const academicYearId = classItem?.academicYear?._id || classItem?.academicYearId;
      
      if (!academicYearId) {
        alert('Academic operation failure: Registration Lifecycle parameters unresolved.');
        setCheckingAssignment(false);
        return;
      }
      
      const apiEndpoint = isEndExam 
        ? '/api/faculty/end-exam-paper-templates/simple'
        : '/api/faculty/paper-template-assignment/simple';
      
      console.log(`[AssessmentStructureViewer] Creating simple assignment - isEndExam: ${isEndExam}, endpoint: ${apiEndpoint}`);
      console.log(`[AssessmentStructureViewer] Component ID being sent: ${component.id || component._id}`);
      console.log(`[AssessmentStructureViewer] Item ID being sent: ${item?.id || item?._id || ''}`);
      
      const payload = {
        componentId: component.id || component._id,
        itemId: item?.id || item?._id || '',
        classId: classId,
        subjectId: subjectId,
        subjectName: subjectName || subject?.name || '',
        subjectCode: subject?.code || subject?.subjectCode || '',
        facultyId: facultyId,
        tenantId: tenantId,
        academicYearId: academicYearId,
        programTypeId: finalProgramTypeId,
        programId: finalProgramId,
        termId: classItem?.termInfo?._id || classItem?.termId || null,
        semId: classItem?.semesterInfo?._id || classItem?.semesterId || null,
        cieTemplateId: null,
        totalMaxMarks: item?.referenceMaxMarks || item?.actualMaxMarks || component?.weightageMarks || 100,
      };
      
      console.log('[AssessmentStructureViewer] Payload:', payload);
      
      const response = await api.post(apiEndpoint, payload);
      
      if (response.data.success) {
        const newAssignment = response.data.assignment;
        const key = `${component.id || component._id}__${item?.id || item?._id || ''}`;
        console.log(`[AssessmentStructureViewer] ✅ Simple assignment created: ${newAssignment._id}`);
        console.log(`[AssessmentStructureViewer] Assignment details:`, {
          id: newAssignment._id,
          isSimple: newAssignment.isSimple,
          isEndExam: newAssignment.isEndExam || isEndExam,
          termId: newAssignment.termId,
          semId: newAssignment.semId
        });
        
        setAssignmentsByKey(prev => ({ 
          ...prev, 
          [key]: {
            ...newAssignment,
            hasMarksEntered: false,
            isEndExam: isEndExam
          }
        }));
        
        navigateToMarksEntry(newAssignment, component.id || component._id, item?.id || item?._id, false, isEndExam);
      } else {
        throw new Error(response.data.message || 'Failed to create assignment');
      }
    } catch (error) {
      console.error('[AssessmentStructureViewer] ❌ Error establishing template track:', error);
      
      if (error.response?.status === 404) {
        setFeedback({
          open: true,
          message: 'The end exam service endpoint is not available. Please contact system administrator.'
        });
      } else if (error.response?.data?.code === 'END_EXAM_TEMPLATE_REQUIRED') {
        setFeedback({
          open: true,
          message: error.response?.data?.message || 'This component requires paper template configuration before marks entry. Please configure the paper template first.'
        });
      } else if (error.response?.status === 400) {
        setFeedback({
          open: true,
          message: 'Invalid request: ' + (error.response?.data?.message || 'Please check the provided data.')
        });
      } else {
        setFeedback({
          open: true,
          message: 'Failed to create assignment: ' + (error.response?.data?.message || error.message)
        });
      }
    } finally {
      setCheckingAssignment(false);
    }
  };

  const handleConfigurePaper = async (component, item = null, isEndExam = false) => {
    console.log(`[AssessmentStructureViewer] 🔧 handleConfigurePaper called - isEndExam: ${isEndExam}, component:`, component?.name);
    console.log(`[AssessmentStructureViewer] 📦 component.requirePaperTemplate: ${component?.requirePaperTemplate}, item?.requirePaperTemplate: ${item?.requirePaperTemplate}`);
    console.log(`[AssessmentStructureViewer] Component ID being used: ${component.id || component._id}`);

    const finalProgramTypeId = localProgramTypeId || programTypeId;
    const finalProgramId = localProgramId || programId;
    const tenantId = classItem?.academicYear?.tenantId || classItem?.tenantId || localStorage.getItem('tenantId');
    const classId = classItem?.classId;
    
    if (!tenantId || !classId || !finalProgramTypeId || !finalProgramId) {
      alert('Academic structural operation tracking mismatch context profiles.');
      return;
    }

    const context = {
      tenantId,
      organizationId: classItem?.organizationId,
      academicYearId: classItem?.academicYear?._id || classItem?.academicYearId,
      semId: classItem?.semesterInfo?._id,
      termId: classItem?.termInfo?._id || classItem?.termId,
      programTypeId: finalProgramTypeId,
      programId: finalProgramId,
      classId,
      subjectId,
      subjectName: subject?.name,
      subjectCode: subject?.code,
      facultyId,
      componentId: component.id || component._id,
      itemId: item?.id || item?._id || null,
      status: 'active',
      isEndExam: isEndExam,
      // ✅ Pass batch info for context
      batchId: batchId,
      batchNumber: batchNumber
    };
    
    const existingAssignment = await (async () => {
      setCheckingAssignment(true);
      try {
        const assignmentEndpoint = isEndExam
          ? '/api/faculty/end-exam-paper-templates/assignment'
          : '/api/faculty/paper-template-assignment';
        
        console.log(`[AssessmentStructureViewer] Checking existing assignment at: ${assignmentEndpoint} with params:`, {
          componentId: context.componentId,
          itemId: context.itemId,
          classId: classId,
          subjectId: subjectId
        });
        
        const response = await api.get(assignmentEndpoint, {
          params: { 
            componentId: String(context.componentId), 
            itemId: item?.id || item?._id || null, 
            classId: String(classId), 
            subjectId: String(subjectId) 
          }
        });
        console.log(`[AssessmentStructureViewer] Existing assignment response:`, response.data);
        return response.data.success ? response.data.assignment : null;
      } catch (err) {
        console.log(`[AssessmentStructureViewer] Error checking existing assignment:`, err.message);
        return null;
      } finally {
        setCheckingAssignment(false);
      }
    })();
    
    const isPaperTemplateRequired =
      (item?.requirePaperTemplate === true) || (component?.requirePaperTemplate === true);
    const canUpgradeSimpleToTemplate = isPaperTemplateRequired && existingAssignment && (
      existingAssignment.isSimple === true ||
      !existingAssignment.templateId ||
      !Array.isArray(existingAssignment.sections) ||
      existingAssignment.sections.length === 0
    );

    if (existingAssignment && existingAssignment.isFinalized && existingAssignment.status !== 'suspended' && !canUpgradeSimpleToTemplate) {
      setFeedback({
        open: true,
        message: 'This course node parameter configuration is committed and immutable. Changes require administrator authorization.'
      });
      return;
    }
    
    if (existingAssignment && !existingAssignment.isFinalized && existingAssignment.templateId) {
      try {
        const response = await api.get(`/api/coe/exam-paper-templates/${existingAssignment.templateId}`);
        setCurrentAssignment(existingAssignment);
        setSelectedTemplate(response.data.template);
        setPaperContext(context);
        setQuestionEditorOpen(true);
      } catch (err) {
        console.error('Failed to parse active blueprint context details:', err);
      }
      return;
    }
    
    console.log(`[AssessmentStructureViewer] Opening PaperTemplateModal - isEndExam: ${isEndExam}`);
    setPaperContext(context);
    setTemplateModalOpen(true);
  };

  const handleTemplateSelected = (template) => {
    setCurrentAssignment(null);
    setSelectedTemplate(template);
    setQuestionEditorOpen(true);
  };

  const handleUpdateAssignment = useCallback((componentId, itemId, assignmentData) => {
    const key = `${componentId}__${itemId || ''}`;
    setAssignmentsByKey(prev => ({
      ...prev,
      [key]: assignmentData
    }));
  }, []);

  const handleSaveAndFinalize = (finalizedAssignment) => {
    if (finalizedAssignment) {
      handleUpdateAssignment(
        finalizedAssignment.componentId,
        finalizedAssignment.itemId,
        finalizedAssignment
      );
    }
    fetchAssessmentStructure();
    setFeedback({ open: true, message: 'Assessment blueprint scheme committed successfully.' });
  };

  if (!isActive) return null;
  if (!classItem?.classId) {
    return (
      <div className="p-5 text-center text-xs font-medium text-slate-400 bg-slate-50 border border-slate-200 border-dashed rounded-lg">
        Course curriculum mapping matrices are unresolved.
      </div>
    );
  }

  return (
    <div className="font-sans antialiased text-slate-900 bg-white space-y-5 max-w-full">
      
      <PaperTemplateModal
        open={templateModalOpen}
        onClose={() => {
          console.log('[AssessmentStructureViewer] 🔴 Closing PaperTemplateModal');
          setTemplateModalOpen(false);
        }}
        context={paperContext}
        onTemplateSelected={(template) => {
          console.log('[AssessmentStructureViewer] ✅ Template selected:', template.name, 'Type:', template.templateType);
          handleTemplateSelected(template);
        }}
        isEndExam={paperContext.isEndExam === true}
      />

      <QuestionEditorModal
        open={questionEditorOpen}
        onClose={() => {
          setQuestionEditorOpen(false);
          setCurrentAssignment(null);
          setSelectedTemplate(null);
        }}
        assignment={currentAssignment}
        template={selectedTemplate}
        context={paperContext}
        onSaveAndFinalize={handleSaveAndFinalize}
        onUpdateAssignment={handleUpdateAssignment}
      />
      
      <SimpleMarksEntryModal
        open={!!simpleMarksAssignment}
        onClose={() => setSimpleMarksAssignment(null)}
        assignment={simpleMarksAssignment}
        onSave={() => {
          setSimpleMarksAssignment(null);
          fetchAssessmentStructure();
        }}
        isEndExam={isEndExamAssignment}
      />
      
      <FeedbackModal
        open={feedback.open}
        message={feedback.message}
        onClose={() => setFeedback({ open: false, message: '' })}
      />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 border border-slate-100 rounded-xl bg-slate-50/50">
          <Loader2 className="w-5 h-5 text-indigo-600 animate-spin mb-2" />
          <p className="text-[10px] font-mono font-bold tracking-widest text-slate-400 uppercase">Compiling System Blueprint Matrix...</p>
        </div>
      ) : error ? (
        <div className="border border-slate-200 rounded-xl p-5 text-center bg-white shadow-sm">
          <p className="text-xs font-medium text-slate-600">{error}</p>
          <button 
            onClick={fetchAssessmentStructure}
            className="mt-3 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
          >
            Re-Index Architecture
          </button>
        </div>
      ) : structure ? (
        <div className="space-y-5">
          
          {/* Dashboard Premium Metric Header Bar */}
          <div className="border border-slate-100 rounded-xl p-4 bg-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg hidden sm:block">
                <BookOpen className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-sm font-bold tracking-tight text-slate-900 uppercase">{subjectName || 'Course Framework'}</h2>
                <span className="text-[10px] font-mono font-bold tracking-widest text-slate-400 uppercase mt-0.5 block">
                  Structure & Grading Ledger Controller
                </span>
                {/* ✅ Show batch info if lab */}
                {labBatch && (
                  <span className="text-[9px] font-mono font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                    Batch {labBatch.batchNumber || 'Unknown'} ({batchId ? 'Filtered' : 'No filter'})
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-6 border-t md:border-t-0 pt-3 md:pt-0 font-mono text-xs text-slate-600">
              <div>
                <span className="text-[9px] font-sans font-bold text-slate-400 uppercase tracking-wider block">Subject Total</span>
                <span className="text-sm font-extrabold text-slate-900">{structure.subject?.totalMarks || '—'}</span>
              </div>
              {structure.subject?.assessmentType !== 'external_only' && (
                <div className="border-l border-slate-100 pl-4">
                  <span className="text-[9px] font-sans font-bold text-slate-400 uppercase tracking-wider block">Subject CIE Marks</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {structure.subject?.cieMaxMarks || '—'}
                  </span>
                </div>
              )}
              {structure.subject?.assessmentType !== 'internal_only' && structure.endExam && (
                <div className="border-l border-slate-100 pl-4">
                  <span className="text-[9px] font-sans font-bold text-slate-400 uppercase tracking-wider block">{endExamValueLabel}</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {structure.subject?.endExamMaxMarks || '—'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* CIE Module Blocks View - Rest of the component remains the same */}
          {structure.components && structure.components.filter(c => c.visibility?.isVisibleToFaculty !== false).length > 0 && (
            <div className="space-y-2.5">
              <div className="flex items-center gap-1.5 pl-0.5 pb-0.5">
                <PieChart className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Continuous Internal Evaluation (CIE) Blocks</span>
              </div>

              <div className="space-y-2">
                {structure.components.filter(c => c.visibility?.isVisibleToFaculty !== false).map((component, idx) => {
                  const componentId = component.componentId || component.id || idx;
                  return (
                    <div key={componentId} className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-sm">
                      
                      <div 
                        onClick={() => toggleComponent(componentId)}
                        className="px-4 py-3 bg-slate-50/60 hover:bg-slate-50 flex items-center justify-between gap-4 cursor-pointer transition-colors border-b border-slate-100 select-none"
                      >
                        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="font-bold text-slate-800 text-xs tracking-tight uppercase">{component.name}</span>
                          <div className="flex items-center gap-2 font-mono text-[10px] font-bold text-slate-400">
                            <span className="text-slate-500 bg-white border border-slate-200/80 px-1.5 py-0.5 rounded">Weight: {component.percentageOfCie}%</span>
                            <span className="text-indigo-600 bg-indigo-50/60 px-1.5 py-0.5 rounded font-sans font-bold">Component Max: {component.allocatedMarks}M</span>
                          </div>
                          {component.isFrozen && (
                            <span className="text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider scale-95 flex items-center gap-0.5">
                              <Lock size={10} /> Locked
                            </span>
                          )}
                        </div>
                        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${expandedComponents[componentId] ? 'rotate-180' : ''}`} />
                      </div>

                      {expandedComponents[componentId] && (
                        <div className="bg-white divide-y divide-slate-100 px-1">
                          {component.type === 'multiple' && component.items?.length > 0 && (
                            component.items.map((item, itemIdx) => {
                              const normalizedComponentId = component.componentId || component.id || component._id;
                              const normalizedItemId = item.itemId || item.id || item._id;
                              const assignmentKey = `${String(normalizedComponentId)}__${String(normalizedItemId || '')}`;
                              const assignment = assignmentsByKey[assignmentKey];
                              const deadline = item.endDate;
                              const deadlinePassed = deadline ? new Date(deadline).getTime() < Date.now() : false;
                              
                              const isFrozenWithAssignment = item.isFrozen && assignment;
                              const hasMarks = assignment?.hasMarksEntered === true;
                              const showReadOnlyView = isFrozenWithAssignment && hasMarks;
                              const showLockedBadge = item.isFrozen && (!assignment || !hasMarks);
                              
                              return (
                                <div key={item.id || itemIdx} className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                                      <span className="font-semibold text-slate-800 text-xs">{item.name}</span>
                                      <span className="font-mono text-[10px] text-slate-400 font-bold">Entry Scale: {item.actualMaxMarks}M</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-slate-400 mt-1 font-medium">
                                      {deadline && (
                                        <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                          <Calendar size={11} className="text-amber-500" /> 
                                          Deadline: {formatDate(deadline)}
                                          {deadlinePassed && <span className="text-red-500 ml-1">(Passed)</span>}
                                        </span>
                                      )}
                                      {item.referenceMaxMarks !== item.actualMaxMarks && (
                                        <span className="text-slate-400 font-mono">Syllabus Baseline: {item.referenceMaxMarks}M</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="shrink-0 flex items-center justify-end pt-0.5 sm:pt-0">
                                    {isPrefetching ? (
                                      <span className="text-[10px] text-slate-400 font-mono tracking-wider uppercase bg-slate-50 px-2 py-0.5 border border-slate-100 rounded">Syncing...</span>
                                    ) : showReadOnlyView ? (
                                      <button
                                        className="h-7 px-3 bg-slate-600 text-white rounded-lg text-xs font-semibold hover:bg-slate-700 transition shadow-sm flex items-center gap-1.5"
                                        onClick={() => navigateToMarksEntry(assignment, normalizedComponentId, normalizedItemId, true, false)}
                                      >
                                        <Eye className="w-3 h-3" /> View Marks
                                      </button>
                                    ) : assignment && assignment.isFinalized ? (
                                      deadlinePassed ? (
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border">Closed</span>
                                      ) : (
                                        <button
                                          className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
                                          onClick={() => navigateToMarksEntry(assignment, normalizedComponentId, normalizedItemId, false, false)}
                                        >
                                          Enter Grades
                                        </button>
                                      )
                                    ) : assignment && assignment.status === 'suspended' ? (
                                      <button
                                        className="h-7 px-3 border border-amber-200 text-amber-700 bg-amber-50 rounded-lg text-xs font-semibold hover:bg-amber-100/70 transition"
                                        onClick={() => handleConfigurePaper(component, item, false)}
                                        disabled={checkingAssignment}
                                      >
                                        Reconfigure Blueprint
                                      </button>
                                    ) : showLockedBadge ? (
                                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border flex items-center gap-1">
                                        <Lock size={10} /> Locked
                                      </span>
                                    ) : item.requirePaperTemplate === true ? (
                                      <button
                                        className="h-7 px-3 bg-slate-800 text-white rounded-lg text-xs font-semibold hover:bg-slate-700 transition tracking-wide"
                                        onClick={() => handleConfigurePaper(component, item, false)}
                                        disabled={checkingAssignment}
                                      >
                                        Configure Paper
                                      </button>
                                    ) : (
                                      <button
                                        className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
                                        onClick={() => createAndNavigateToMarksEntry(component, item, false)}
                                        disabled={checkingAssignment}
                                      >
                                        Open Ledger
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}

                          {component.type === 'single' && component.items?.length > 0 && (
                            <div className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                                  <span className="font-semibold text-slate-800 text-xs">{component.name} Core Item</span>
                                  <span className="font-mono text-[10px] text-slate-400 font-bold">Entry Scale: {component.items[0]?.actualMaxMarks || '—'}M</span>
                                </div>
                                {(() => {
                                  const deadline = component.items[0]?.endDate || component.singleItemEndDate || component.endDate;
                                  const deadlinePassed = deadline ? new Date(deadline).getTime() < Date.now() : false;
                                  return deadline && (
                                    <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1 font-medium">
                                      <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                        <Calendar size={11} className="text-amber-500" /> 
                                        Deadline: {formatDate(deadline)}
                                        {deadlinePassed && <span className="text-red-500 ml-1">(Passed)</span>}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                              
                              <div className="shrink-0 flex items-center justify-end pt-0.5 sm:pt-0">
                                {(() => {
                                  const normalizedComponentId = component.componentId || component.id || component._id;
                                  const assignmentKey = `${String(normalizedComponentId)}__`;
                                  const assignment = assignmentsByKey[assignmentKey];
                                  const deadline = component.items[0]?.endDate || component.singleItemEndDate || component.endDate;
                                  const deadlinePassed = deadline ? new Date(deadline).getTime() < Date.now() : false;
                                  
                                  const isFrozenWithAssignment = component.isFrozen && assignment;
                                  const hasMarks = assignment?.hasMarksEntered === true;
                                  const showReadOnlyView = isFrozenWithAssignment && hasMarks;
                                  const showLockedBadge = component.isFrozen && (!assignment || !hasMarks);
                                  
                                  if (isPrefetching) {
                                    return <span className="text-[10px] text-slate-400 font-mono tracking-wider uppercase bg-slate-50 px-2 py-0.5 border border-slate-100 rounded">Syncing...</span>;
                                  }
                                  if (showReadOnlyView) {
                                    return (
                                      <button
                                        className="h-7 px-3 bg-slate-600 text-white rounded-lg text-xs font-semibold hover:bg-slate-700 transition shadow-sm flex items-center gap-1.5"
                                        onClick={() => navigateToMarksEntry(assignment, normalizedComponentId, null, true, false)}
                                      >
                                        <Eye className="w-3 h-3" /> View Marks
                                      </button>
                                    );
                                  }
                                  if (assignment && assignment.isFinalized) {
                                    return deadlinePassed ? (
                                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border">Closed</span>
                                    ) : (
                                      <button
                                        className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
                                        onClick={() => navigateToMarksEntry(assignment, normalizedComponentId, null, false, false)}
                                      >
                                        Enter Grades
                                      </button>
                                    );
                                  } else if (assignment && assignment.status === 'suspended') {
                                    return (
                                      <button
                                        className="h-7 px-3 border border-amber-200 text-amber-700 bg-amber-50 rounded-lg text-xs font-semibold hover:bg-amber-100/70 transition"
                                        onClick={() => handleConfigurePaper(component, null, false)}
                                        disabled={checkingAssignment}
                                      >
                                        Reconfigure Blueprint
                                      </button>
                                    );
                                  } else if (showLockedBadge) {
                                    return (
                                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border flex items-center gap-1">
                                        <Lock size={10} /> Locked
                                      </span>
                                    );
                                  } else if (component.requirePaperTemplate === true) {
                                    return (
                                      <button
                                        className="h-7 px-3 bg-slate-800 text-white rounded-lg text-xs font-semibold hover:bg-slate-700 transition tracking-wide"
                                        onClick={() => handleConfigurePaper(component, null, false)}
                                        disabled={checkingAssignment}
                                      >
                                        Configure Paper
                                      </button>
                                    );
                                  } else {
                                    return (
                                      <button
                                        className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
                                        onClick={() => createAndNavigateToMarksEntry(component, component.items[0], false)}
                                        disabled={checkingAssignment}
                                      >
                                        Open Ledger
                                      </button>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          )}

                          {component.aggregationRule && (
                            <div className="p-3 text-[10px] text-slate-400 border-t border-slate-100 font-mono tracking-wide uppercase">
                              Mapping Rules Engine Statement: {component.aggregationRule.description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summative End Exam Panel - Rest remains the same */}
          {structure.endExam && structure.endExam.isVisibleToFaculty !== false && (
            (() => {
              const endExamComponentId = String(structure.endExam.componentId || structure.endExam.id || structure.endExam._id || '');
              const hasMultipleItems = structure.endExam.hasMultipleItems === true;
              const endExamItems = structure.endExam.items || [];
              const aggregationRule = structure.endExam.aggregationRule;
              const endExamDeadline = structure.endExam.endDate;
              const endExamDeadlinePassed = endExamDeadline ? new Date(endExamDeadline).getTime() < Date.now() : false;
              const requiresPaperTemplate = structure.endExam.requirePaperTemplate === true;
              
              const endExamComponentRef = {
                id: endExamComponentId,
                _id: endExamComponentId,
                name: structure.endExam.name,
                requirePaperTemplate: structure.endExam.requirePaperTemplate === true,
                isFrozen: structure.endExam.isFrozen,
                weightageMarks: structure.endExam.weightageMarks,
                componentId: endExamComponentId
              };

              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 pl-0.5 pb-0.5">
                    <Award className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{endExamTitle}</span>
                    {hasMultipleItems && endExamItems.length > 0 && (
                      <span className="text-[9px] font-mono bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                        {endExamItems.length} items · {structure.endExam.totalMaxMarks || endExamItems.reduce((s, i) => s + (i.referenceMaxMarks || 0), 0)} marks
                      </span>
                    )}
                    {aggregationRule && hasMultipleItems && (
                      <span className="text-[9px] font-mono bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                        Aggregation: {aggregationRule.type} {aggregationRule.nValue ? `(N=${aggregationRule.nValue})` : ''}
                      </span>
                    )}
                  </div>

                  <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-sm">
                    <div 
                      onClick={() => toggleComponent('end-exam')}
                      className="px-4 py-3 bg-slate-50/60 hover:bg-slate-50 flex items-center justify-between gap-4 cursor-pointer transition-colors border-b border-slate-100 select-none"
                    >
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="font-bold text-slate-800 text-xs tracking-tight uppercase">{structure.endExam.name}</span>
                        <div className="flex items-center gap-2 font-mono text-[10px] font-bold text-slate-400">
                          <span className="text-indigo-600 bg-indigo-50/60 px-1.5 py-0.5 rounded font-sans font-bold">Subject Max: {structure.subject?.endExamMaxMarks || structure.endExam.weightageMarks || '—'}M</span>
                        </div>
                        {structure.endExam.isFrozen && (
                          <span className="text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider scale-95 flex items-center gap-0.5">
                            <Lock size={10} /> Locked
                          </span>
                        )}
                        {endExamDeadline && endExamDeadlinePassed && !structure.endExam.isFrozen && (
                          <span className="text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">
                            Deadline Passed
                          </span>
                        )}
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${expandedComponents['end-exam'] ? 'rotate-180' : ''}`} />
                    </div>

                    {expandedComponents['end-exam'] && (
                      <div className="bg-white">
                        {endExamDeadline && (
                          <div className="px-4 pt-3 pb-2 border-b border-slate-100">
                            <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-1 rounded text-[10px] font-medium w-fit">
                              <Calendar size={11} className="text-amber-500" />
                              Overall Deadline: {formatDate(endExamDeadline)}
                              {endExamDeadlinePassed && <span className="text-red-500 ml-1">(Passed)</span>}
                            </span>
                          </div>
                        )}

                        {hasMultipleItems && endExamItems.length > 0 && (
                          <div className="divide-y divide-slate-100">
                            {endExamItems.map((item, idx) => {
                              const normalizedItemId = item.itemId || item.id || item._id;
                              const assignmentKey = `${endExamComponentId}__${String(normalizedItemId || '')}`;
                              const assignment = assignmentsByKey[assignmentKey];
                              const itemDeadline = item.endDate;
                              const itemDeadlinePassed = itemDeadline ? new Date(itemDeadline).getTime() < Date.now() : false;
                              const isExpanded = expandedEndExamItems[item.id || item.itemId || idx] || false;
                              
                              const isFrozenWithAssignment = (item.isFrozen || structure.endExam.isFrozen) && assignment;
                              const hasMarks = assignment?.hasMarksEntered === true;
                              const showReadOnlyView = isFrozenWithAssignment && hasMarks;
                              const showLockedBadge = (item.isFrozen || structure.endExam.isFrozen) && (!assignment || !hasMarks);
                              const itemRequiresPaperTemplate = item.requirePaperTemplate === true;
                              
                              const hasConfiguredTemplate = !!(
                                assignment &&
                                assignment.templateId &&
                                assignment.isSimple !== true &&
                                assignment.isFinalized === true
                              );
                              const shouldForceConfigure = itemRequiresPaperTemplate && !hasConfiguredTemplate;
                              const needsTemplateUpgrade = assignment && assignment.isSimple === true && itemRequiresPaperTemplate && !hasConfiguredTemplate;

                              const endExamItemRef = {
                                id: item.id || item.itemId,
                                _id: item.id || item.itemId,
                                name: item.name,
                                referenceMaxMarks: item.referenceMaxMarks || item.actualMaxMarks || 0,
                                actualMaxMarks: item.referenceMaxMarks || item.actualMaxMarks || 0,
                                requirePaperTemplate: itemRequiresPaperTemplate
                              };

                              return (
                                <div key={item.id || item.itemId || idx} className="border-b border-slate-100 last:border-b-0">
                                  <div 
                                    onClick={() => toggleEndExamItem(item.id || item.itemId || idx)}
                                    className="px-4 py-3 bg-white hover:bg-slate-50/50 cursor-pointer transition-colors"
                                  >
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center gap-3 flex-wrap">
                                        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : '-rotate-90'}`} />
                                        <span className="font-medium text-slate-800 text-sm">{item.name}</span>
                                        <span className="text-xs text-slate-500 font-mono">Item Max: {item.referenceMaxMarks || item.actualMaxMarks || 0} marks</span>
                                        {item.isFrozen && (
                                          <span className="text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-[9px] font-bold flex items-center gap-0.5">
                                            <Lock size={10} /> Frozen
                                          </span>
                                        )}
                                        {itemDeadline && itemDeadlinePassed && !item.isFrozen && (
                                          <span className="text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded text-[9px] font-bold">
                                            Deadline Passed
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {isPrefetching ? (
                                          <span className="text-[10px] text-slate-400 px-2 py-0.5 bg-slate-50 rounded">Syncing...</span>
                                        ) : showReadOnlyView ? (
                                          <button
                                            className="h-6 px-2 bg-slate-600 text-white rounded text-[10px] font-semibold hover:bg-slate-700 transition flex items-center gap-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigateToMarksEntry(assignment, endExamComponentId, item.id || item.itemId, true, true);
                                            }}
                                          >
                                            <Eye size={12} /> View
                                          </button>
                                        ) : assignment && assignment.isFinalized && (!itemRequiresPaperTemplate || hasConfiguredTemplate) ? (
                                          <button
                                            className="h-6 px-2 bg-indigo-600 text-white rounded text-[10px] font-semibold hover:bg-indigo-700 transition"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigateToMarksEntry(assignment, endExamComponentId, item.id || item.itemId, false, true);
                                            }}
                                          >
                                            Enter Grades
                                          </button>
                                        ) : shouldForceConfigure ? (
                                          <button
                                            className="h-6 px-2 bg-slate-800 text-white rounded text-[10px] font-semibold hover:bg-slate-700 transition"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleConfigurePaper(endExamComponentRef, endExamItemRef, true);
                                            }}
                                            disabled={checkingAssignment}
                                          >
                                            Configure Paper
                                          </button>
                                        ) : needsTemplateUpgrade ? (
                                          <button
                                            className="h-6 px-2 bg-amber-600 text-white rounded text-[10px] font-semibold hover:bg-amber-700 transition"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleConfigurePaper(endExamComponentRef, endExamItemRef, true);
                                            }}
                                            disabled={checkingAssignment}
                                          >
                                            Upgrade
                                          </button>
                                        ) : showLockedBadge ? (
                                          <span className="text-[10px] text-slate-400 font-bold uppercase bg-slate-50 px-2 py-0.5 rounded border flex items-center gap-1">
                                            <Lock size={10} /> Locked
                                          </span>
                                        ) : (
                                          <button
                                            className="h-6 px-2 bg-indigo-600 text-white rounded text-[10px] font-semibold hover:bg-indigo-700 transition"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              createAndNavigateToMarksEntry(endExamComponentRef, endExamItemRef, true);
                                            }}
                                            disabled={checkingAssignment}
                                          >
                                            Open Ledger
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {isExpanded && (
                                    <div className="px-4 pb-3 pt-0 bg-slate-50/30 border-t border-slate-100">
                                      {itemDeadline && (
                                        <div className="mt-2 text-[10px] text-amber-600 flex items-center gap-1">
                                          <Calendar size={10} />
                                          Item Deadline: {formatDate(itemDeadline)}
                                          {itemDeadlinePassed && <span className="text-red-500 ml-1">(Passed)</span>}
                                        </div>
                                      )}
                                      {item.requirePaperTemplate && (
                                        <div className="mt-1 text-[10px] text-purple-600 flex items-center gap-1">
                                          <Layers size={10} />
                                          Requires Paper Template Configuration
                                        </div>
                                      )}
                                      {assignment && assignment.isFinalized && (
                                        <div className="mt-1 text-[10px] text-green-600 flex items-center gap-1">
                                          <CheckCircle size={10} />
                                          Paper Template Configured & Finalized
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            
                            {aggregationRule && (
                              <div className="px-4 py-2 bg-indigo-50/30 text-[10px] text-indigo-700 font-mono border-t border-indigo-100">
                                🔗 Aggregation Rule: {aggregationRule.type === 'sum_of_all' ? 'Sum of all items' : 
                                  aggregationRule.type === 'average_of_all' ? 'Average of all items' :
                                  aggregationRule.type === 'best_of_n' ? `Best ${aggregationRule.nValue} out of ${endExamItems.length} items` :
                                  `${aggregationRule.type}`}
                              </div>
                            )}
                          </div>
                        )}

                        {!hasMultipleItems && (
                          <div className="p-4">
                            {(() => {
                              const endExamAssignmentKey = `${endExamComponentId}__`;
                              const endExamAssignment = assignmentsByKey[endExamAssignmentKey];
                              const showReadOnlyView = structure.endExam.isFrozen && endExamAssignment?.hasMarksEntered;
                              const showLockedBadge = structure.endExam.isFrozen && (!endExamAssignment || !endExamAssignment?.hasMarksEntered);
                              const hasConfiguredTemplate = !!(
                                endExamAssignment &&
                                endExamAssignment.templateId &&
                                endExamAssignment.isSimple !== true &&
                                endExamAssignment.isFinalized === true &&
                                Array.isArray(endExamAssignment.sections) &&
                                endExamAssignment.sections.length > 0
                              );
                              const shouldForceConfigure = requiresPaperTemplate && !hasConfiguredTemplate;
                              const needsTemplateUpgrade = endExamAssignment && endExamAssignment.isSimple === true && requiresPaperTemplate && !hasConfiguredTemplate;

                              return (
                                <div className="flex items-center justify-end">
                                  {isPrefetching ? (
                                    <span className="text-[10px] text-slate-400 font-mono tracking-wider uppercase bg-slate-50 px-2 py-0.5 border border-slate-100 rounded">Syncing...</span>
                                  ) : showReadOnlyView ? (
                                    <button
                                      className="h-7 px-3 bg-slate-600 text-white rounded-lg text-xs font-semibold hover:bg-slate-700 transition shadow-sm flex items-center gap-1.5"
                                      onClick={() => navigateToMarksEntry(endExamAssignment, endExamComponentId, null, true, true)}
                                    >
                                      <Eye className="w-3 h-3" /> View Marks
                                    </button>
                                  ) : needsTemplateUpgrade ? (
                                    <button
                                      className="h-7 px-3 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition shadow-sm flex items-center gap-1.5"
                                      onClick={() => handleConfigurePaper(endExamComponentRef, null, true)}
                                      disabled={checkingAssignment}
                                    >
                                      Upgrade to Paper Template
                                    </button>
                                  ) : endExamAssignment && endExamAssignment.status === 'suspended' ? (
                                    <button
                                      className="h-7 px-3 border border-amber-200 text-amber-700 bg-amber-50 rounded-lg text-xs font-semibold hover:bg-amber-100/70 transition"
                                      onClick={() => handleConfigurePaper(endExamComponentRef, null, true)}
                                      disabled={checkingAssignment || structure.endExam.allowFacultyEdit === false}
                                    >
                                      Reconfigure Blueprint
                                    </button>
                                  ) : showLockedBadge ? (
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border flex items-center gap-1">
                                      <Lock size={10} /> Locked
                                    </span>
                                  ) : structure.endExam.allowFacultyEdit === false ? (
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border">Read Only</span>
                                  ) : endExamDeadlinePassed ? (
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50 px-2 py-1 rounded border">Closed</span>
                                  ) : shouldForceConfigure ? (
                                    <button
                                      className="h-7 px-3 bg-slate-800 text-white rounded-lg text-xs font-semibold hover:bg-slate-700 transition tracking-wide"
                                      onClick={() => handleConfigurePaper(endExamComponentRef, null, true)}
                                      disabled={checkingAssignment}
                                    >
                                      Configure Paper
                                    </button>
                                  ) : endExamAssignment && endExamAssignment.isFinalized && (!requiresPaperTemplate || hasConfiguredTemplate) ? (
                                    <button
                                      className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
                                      onClick={() => navigateToMarksEntry(endExamAssignment, endExamComponentId, null, false, true)}
                                    >
                                      Enter Grades
                                    </button>
                                  ) : (
                                    <button
                                      className="h-7 px-3 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
                                      onClick={() => createAndNavigateToMarksEntry(
                                        endExamComponentRef,
                                        { referenceMaxMarks: structure.endExam.weightageMarks, requirePaperTemplate: requiresPaperTemplate },
                                        true
                                      )}
                                      disabled={checkingAssignment}
                                    >
                                      Open Ledger
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AssessmentStructureViewer;